import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../redux/store';
import { doCreateIssue } from '../../../modules/issues/stores/thunk';

import { Button } from '../../../components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ISSUE_REASONS, IssueReason } from './issue.types';

interface CarrierIssueFormProps {
  userId: string;
  onCancel: () => void;
  onSuccess?: (_payload: any) => void;
}

// Custom SelectContent with higher z-index for modals
const ModalSelectContent = React.forwardRef<
  React.ElementRef<typeof SelectContent>,
  React.ComponentPropsWithoutRef<typeof SelectContent>
>(({ className, ...props }, ref) => <SelectContent ref={ref} className={`z-[999999] ${className || ''}`} {...props} />);
ModalSelectContent.displayName = 'ModalSelectContent';

const CarrierIssueForm: React.FC<CarrierIssueFormProps> = ({ userId, onCancel, onSuccess }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [currentLength, setCurrentLength] = useState(0);

  const formSchema = z.object({
    reason: z.enum(ISSUE_REASONS.map((r) => r.value) as [IssueReason, ...IssueReason[]], {
      message: 'Please select a reason',
    }),
    message: z.string().min(1, { message: 'Please describe the issue' }).max(500, {
      message: 'Message cannot exceed 500 characters',
    }),
  });

  type FormValues = z.infer<typeof formSchema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reason: undefined,
      message: '',
    },
  });

  const handleMessageChange = (value: string) => {
    setCurrentLength(value.length);
  };

  const onSubmit = async (values: FormValues) => {
    const { payload } = await dispatch(
      doCreateIssue({
        creatorUserId: userId,
        ...values,
      }),
    );

    form.reset();

    if (onSuccess) {
      onSuccess(payload);
    }

    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> This form creates a general carrier issue, unrelated to a specific order. If you need
            to report an issue with a specific order, please go to the order page and create the issue there.
          </p>
        </div>

        <FormField
          control={form.control}
          name="reason"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Reason</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                </FormControl>
                <ModalSelectContent>
                  {ISSUE_REASONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </ModalSelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Message</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Please describe the issue"
                  className="resize-none"
                  rows={6}
                  maxLength={500}
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    handleMessageChange(e.target.value);
                  }}
                />
              </FormControl>
              <div className="text-right text-sm text-muted-foreground">{currentLength}/500</div>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end space-x-4 mt-6">
          <Button variant="outline" onClick={onCancel} type="button">
            Cancel
          </Button>

          <Button type="submit" disabled={!form.formState.isValid}>
            Submit Issue
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default CarrierIssueForm;
